import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  TouchableOpacity,
  ImageBackground,
  Dimensions,
  StyleSheet,
  ScrollView,
  Platform,
  Text,
  useWindowDimensions,
  ActivityIndicator,
} from "react-native";
import Lobby, { LobbyRoom } from "../Lobby/LobbyRoom";
import { LeftArrowPng, RightArrowPng } from "../../../assets";
import Carousel from "react-native-reanimated-carousel";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { lobbyRoomsRequest } from "../../../store/lobby/lobbyActions";
import { useNavigation } from "@react-navigation/native";
import { useDevice } from "../../../utils/hooks/useDevice";
import WatchAdButton from "../common/WatchAdButton";

type LobbyRoomType = {
  id: string | number;
  name: string;
  minLevel: number;
  entryFee: number;
  isVip: boolean;
  activeTablesCount: number;
};

const PAGE_SIZE = 3;

// Web-compatible carousel component
// const WebCarousel = ({ data, renderItem, onScrollEnd }) => {
//   const scrollViewRef = useRef(null);
//   const [currentIndex, setCurrentIndex] = useState(0);

//   const handleScroll = (event) => {
//     const newIndex = Math.round(event.nativeEvent.contentOffset.x / width);
//     setCurrentIndex(newIndex);
//     if (onScrollEnd) onScrollEnd();
//   };

//   return (
//     <ScrollView
//       ref={scrollViewRef}
//       horizontal
//       pagingEnabled
//       showsHorizontalScrollIndicator={false}
//       onMomentumScrollEnd={handleScroll}
//       style={styles.scrollView}
//     >
//       {data.map((item, index) => (
//         <View key={item.id} style={styles.slide}>
//           {renderItem({ item, index })}
//         </View>
//       ))}
//     </ScrollView>
//   );
// };

// Native carousel component with reanimated
const NativeCarousel = React.lazy(
  () => import("react-native-reanimated-carousel"),
);

export const LobbyRoomSection = () => {
  const [isClient, setIsClient] = useState(false);
  const [isWeb, setIsWeb] = useState(false);
  const carouselRef = useRef<any>(null);
  const [showArrows, setShowArrows] = useState(true);
  const { isLogged } = useSelector((s: RootState) => s.auth);
  const navigation = useNavigation();
  const { width, height } = useWindowDimensions();

  const {
    isXLg,
    isSm,
    isMobile,
    isTablet,
    isPortrait,
    isLandscape,
    isNative,
    isDesktop,
  } = useDevice();

  // const isPortrait = height >= width;
  const isSmall = isMobile || isTablet || isSm;

  const SMALL_SIZE = isSmall ? width * 0.36 : width * 0.2;
  const LARGE_SIZE = isSmall ? width * 0.55 : width * 0.35;

  const itemsPerPage = isPortrait ? 1 : 3;

  const dispatch = useDispatch();
  const {
    items: rooms,
    loading,
    error,
    total,
    loaded,
    lastFetched,
  } = useSelector((s: RootState) => s.lobby);

  useEffect(() => {
    const CACHE_TTL = 5 * 60 * 1000;

    const isCacheValid =
      loaded &&
      lastFetched !== null &&
      Date.now() - lastFetched < CACHE_TTL &&
      rooms.length > 0;

    if (!isCacheValid) {
      dispatch(lobbyRoomsRequest({ start: 0, limit: 20 }));
    }
  }, [dispatch, loaded, lastFetched, rooms.length]);
  const hasCachedRooms = rooms.length > 0;
  const shouldShowFullLoader = loading && !hasCachedRooms;

  const pages: LobbyRoomType[][] = useMemo(() => {
    const out: LobbyRoomType[][] = [];
    for (let i = 0; i < rooms.length; i += itemsPerPage) {
      out.push(rooms.slice(i, i + itemsPerPage));
    }
    return out;
  }, [rooms, itemsPerPage]);

  const hasMultiplePages = pages.length > 1;
  const isReady = !loading && rooms.length > 0;

  useEffect(() => {
    const web = Platform.OS === "web";
    setIsWeb(web);
    setIsClient(web ? typeof window !== "undefined" : true);
  }, []);

  useEffect(() => {
    if (!isLogged) {
      navigation.navigate("Login" as never);
    }
  }, [isLogged]);

  useEffect(() => {
    dispatch(lobbyRoomsRequest({ start: 0, limit: 20 }));
  }, [dispatch]);

  // const handleNext = () => {
  //   setShowArrows(false);
  //   if (!isWeb && carouselRef.current?.next) {
  //     carouselRef.current.next();
  //   }
  // };

  // const handlePrev = () => {
  //   setShowArrows(false);
  //   if (!isWeb && carouselRef.current?.prev) {
  //     carouselRef.current.prev();
  //   }
  // };

  // const handleScrollEnd = () => {
  //   setShowArrows(true);
  // };

  const handleNext = () => {
    if (!hasMultiplePages) return;
    setShowArrows(false);
    carouselRef.current?.next();
  };

  const handlePrev = () => {
    if (!hasMultiplePages) return;
    setShowArrows(false);
    carouselRef.current?.prev();
  };

  if (!isClient) return null;

  return isLogged ? (
    <View style={[styles.container, { width: width, height: height * 0.8 }]}>
      {shouldShowFullLoader ? (
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ActivityIndicator size="large" color="#B7C1D1" />
        </View>
      ) : rooms.length === 0 ? (
        <View
          style={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text style={{ color: "#fff" }}>No rooms available</Text>
        </View>
      ) : (
        <>
          <View
            style={{
              alignItems: "center",
              marginTop: isMobile ? 70 : 0,
              // height: "100%",
            }}
          >
            <Carousel<LobbyRoomType[]>
              ref={carouselRef}
              loop={false}
              width={width}
              height={
                isMobile
                  ? height * 0.9
                  : isSm
                    ? height * 0.7
                    : isTablet
                      ? height * 0.7
                      : isXLg
                        ? height * 0.75
                        : height * 0.75
              }
              autoPlay={false}
              data={pages}
              pagingEnabled
              scrollAnimationDuration={1200}
              onScrollEnd={() => {
                setShowArrows(true);
                // setScrollLoading(false);
              }}
              snapEnabled={false}
              renderItem={({ item: page, index: pageIndex }) => {
                return (
                  <View
                    key={`page-${pageIndex}`}
                    style={{
                      flexDirection: "row",
                      justifyContent: "center",
                      width: isSmall
                        ? width
                        : // : isTablet
                          //   ? width - 180
                          width - 100,
                      paddingHorizontal: 8,
                      margin: "auto",
                      alignItems: "center",
                      marginTop: isSmall ? -8 : 2,
                      height: isDesktop ? "100%" : "auto",
                    }}
                  >
                    {page.map((room, itemIndex) => {
                      const globalIndex = pageIndex * itemsPerPage + itemIndex;
                      return (
                        <View
                          key={room.id}
                          style={{
                            width: isPortrait
                              ? width
                              : // : isTablet
                                //   ? width / 3 - 70
                                width / 3 - 120,
                            marginHorizontal: 1,
                          }}
                        >
                          <LobbyRoom
                            room={room}
                            index={globalIndex}
                            totalCount={rooms.length}
                          />
                        </View>
                      );
                    })}
                  </View>
                );
              }}
            />
          </View>
          <></>
          {/* {isWeb ? (
        <WebCarousel
          data={carouselData}
          renderItem={() => <Lobby />}
          onScrollEnd={handleScrollEnd}
        />
      ) : (
        <React.Suspense fallback={<View style={styles.loading} />}>
          <NativeCarousel
            ref={carouselRef}
            loop
            width={width}
            height={550}
            autoPlay={false}
            data={carouselData}
            scrollAnimationDuration={1000}
            onScrollEnd={handleScrollEnd}
            renderItem={() => <Lobby />}
          />
        </React.Suspense>
      )} */}

          <TouchableOpacity style={styles.arrowLeft} onPress={handlePrev}>
            <ImageBackground
              source={LeftArrowPng}
              resizeMode="cover"
              style={styles.arrowBg}
              imageStyle={styles.arrowImage}
            />
          </TouchableOpacity>

          <TouchableOpacity style={styles.arrowRight} onPress={handleNext}>
            <ImageBackground
              source={RightArrowPng}
              resizeMode="cover"
              style={styles.arrowBg}
              imageStyle={styles.arrowImage}
            />
          </TouchableOpacity>

          {/* Watch Ad button — shown in lobby for native players (MD §1) */}
          {Platform.OS !== "web" && (
            <View style={styles.watchAdContainer}>
              <WatchAdButton />
            </View>
          )}
        </>
      )}
    </View>
  ) : null;
};

export default LobbyRoomSection;

const styles = StyleSheet.create({
  container: {
    position: "relative",

    height: 450,
  },
  scrollView: {
    flex: 1,
  },
  slide: {
    height: 450,
  },
  loading: {
    // width: width,
    height: 450,
    backgroundColor: "#f0f0f0",
    justifyContent: "center",
    alignItems: "center",
  },
  arrowLeft: {
    position: "absolute",
    left: 20,
    top: "50%",
    transform: [{ translateY: -25 }],
    zIndex: 1,
  },
  arrowRight: {
    position: "absolute",
    right: 20,
    top: "50%",
    transform: [{ translateY: -25 }],
    zIndex: 1,
  },
  arrowBg: {
    width: 50,
    height: 50,
    justifyContent: "center",
    alignItems: "center",
  },
  arrowImage: {
    borderRadius: 25,
  },
  watchAdContainer: {
    position: "absolute",
    bottom: 16,
    left: 80,
    right: 80,
    zIndex: 2,
    alignItems: "center",
  },
});
